import React from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@emotion/react'
import type { GuessFeedback } from '../types'

const pulse = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 1;
  }
  70% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.15);
    opacity: 0.5;
  }
`

type GuessFeedbackPulseProps = {
  feedback?: GuessFeedback | null
}

const COLOR_MAP: Record<GuessFeedback['type'], string> = {
  success: '#0f9d58',
  error: '#c62828',
}

const GuessFeedbackPulse = ({ feedback }: GuessFeedbackPulseProps) => {
  const [visible, setVisible] = React.useState(false)
  const prevId = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (feedback && feedback.id !== prevId.current) {
      prevId.current = feedback.id
      setVisible(true)
      const timeout = window.setTimeout(() => setVisible(false), 600)
      return () => window.clearTimeout(timeout)
    }
    if (!feedback || feedback.id !== prevId.current) {
      setVisible(false)
    }
    return undefined
  }, [feedback])

  if (!feedback || !visible) return null
  const color = COLOR_MAP[feedback.type]

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: 140,
          height: 140,
          maxWidth: '60%',
          maxHeight: '60%',
          borderRadius: '100%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${pulse} 0.6s ease-out`,
          boxShadow: `0 0 30px ${color}55`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', px: 1 }}
        >
          {feedback.message}
        </Typography>
      </Box>
    </Box>
  )
}

export default GuessFeedbackPulse
